/**
 * Moving a chat to another project (Move to…, or M7's one offer). The chat
 * leaves one project's list and joins the other's at once; a refusal puts it
 * back. The API refuses a shared chat (THREAD_SHARED), an archived target
 * (PROJECT_ARCHIVED) and a chat whose reply is still being written.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { WorkshopSession } from "@/types/workshop.type";
import { workshopProjectsKey, workshopSessionsKey } from "./workshopKeys";

type MoveVars = { session: WorkshopSession; toProjectId: string; toProjectName: string };

/** The API's own sentence for a refusal it explains (shared chat, reply running). */
function moveRefusal(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { error?: string; code?: string } | undefined;
    if (data?.code === "PROJECT_ARCHIVED") return "That project is archived. Restore it first.";
    if (data?.error && error.response?.status !== 500) return data.error;
  }
  return getAiRequestErrorMessage(error);
}

export function useMoveSession(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ session, toProjectId }: MoveVars) => {
      await axiosInstance.put(`/workshop/sessions/${session.id}`, { projectId: toProjectId });
    },
    onMutate: async ({ session, toProjectId }) => {
      const from = workshopSessionsKey(session.projectId);
      const to = workshopSessionsKey(toProjectId);
      await Promise.all([qc.cancelQueries({ queryKey: from }), qc.cancelQueries({ queryKey: to })]);
      qc.setQueryData<WorkshopSession[]>(from, (list) => list?.filter((s) => s.id !== session.id));
      qc.setQueryData<WorkshopSession[]>(to, (list) =>
        list ? [{ ...session, projectId: toProjectId }, ...list.filter((s) => s.id !== session.id)] : list,
      );
    },
    // Put back only this chat: both lists may have changed meanwhile.
    onError: (error, { session, toProjectId }) => {
      qc.setQueryData<WorkshopSession[]>(workshopSessionsKey(toProjectId), (list) =>
        list?.filter((s) => s.id !== session.id),
      );
      qc.setQueryData<WorkshopSession[]>(workshopSessionsKey(session.projectId), (list) =>
        list && !list.some((s) => s.id === session.id) ? [session, ...list] : list,
      );
      queue.add({ title: "Couldn't move the chat", description: moveRefusal(error), variant: "error" });
    },
    onSuccess: (_data, { toProjectName }) => {
      queue.add({ title: `Moved to ${toProjectName}`, variant: "success" });
    },
    onSettled: (_data, _error, { session, toProjectId }) => {
      void qc.invalidateQueries({ queryKey: workshopSessionsKey(session.projectId) });
      void qc.invalidateQueries({ queryKey: workshopSessionsKey(toProjectId) });
      void qc.invalidateQueries({ queryKey: workshopProjectsKey(brandId) });
    },
  });
}

